/**
 * Commercial pillar guides — long-form intent hubs at /guides/:slug
 * Separate from /services/* detail pages for topical authority + SERP coverage.
 */
import type { Locale } from '@/lib/serviceCatalog';
import { getServiceBySlug, localizeServiceText } from '@/lib/serviceCatalog';

type L = { tr: string; en: string };

export type PillarGuideFaq = { question: L; answer: L };

export type PillarGuideSection = {
  title: L;
  paragraphs: L[];
  bullets?: L[];
};

export type PillarGuideComparisonRow = {
  aspect: L;
  alternative: L;
  figtures: L;
};

export type PillarGuideTimelinePhase = {
  phase: L;
  duration: L;
  deliverable: L;
};

export type PillarGuide = {
  slug: string;
  title: L;
  seoTitle: L;
  seoDescription: L;
  heroTitle: L;
  directAnswer: L;
  primaryServiceSlug: string;
  relatedServiceSlugs: string[];
  relatedGuideSlugs: string[];
  relatedProjectSlugs: string[];
  relatedBlogSlugs: string[];
  sections: PillarGuideSection[];
  comparisonRows: PillarGuideComparisonRow[];
  timelinePhases: PillarGuideTimelinePhase[];
  faqs: PillarGuideFaq[];
};

export function localizePillarGuideText(value: L, lang: Locale) {
  return value[lang];
}

export function estimateGuideWordCount(guide: PillarGuide, lang: Locale): number {
  const chunks: string[] = [
    guide.directAnswer[lang],
    ...guide.sections.flatMap((s) => [s.title[lang], ...s.paragraphs.map((p) => p[lang]), ...(s.bullets?.map((b) => b[lang]) ?? [])]),
    ...guide.comparisonRows.flatMap((r) => [r.aspect[lang], r.alternative[lang], r.figtures[lang]]),
    ...guide.timelinePhases.flatMap((t) => [t.phase[lang], t.duration[lang], t.deliverable[lang]]),
    ...guide.faqs.flatMap((f) => [f.question[lang], f.answer[lang]]),
  ];
  return chunks.join(' ').split(/\s+/).filter(Boolean).length;
}

const guides: PillarGuide[] = [
  {
    slug: 'custom-software-development-turkey',
    title: {
      tr: 'Türkiye Özel Yazılım Geliştirme Rehberi (2026)',
      en: 'Custom Software Development in Turkey — Complete Guide (2026)',
    },
    seoTitle: {
      tr: 'Türkiye Özel Yazılım Geliştirme: Maliyet, Süreç ve Partner Seçimi | Figtures',
      en: 'Custom Software Development Turkey: Cost, Process & Partner Selection | Figtures',
    },
    seoDescription: {
      tr: 'Türkiye\'de özel yazılım geliştirme maliyeti, süreç, ekip modeli ve doğru product studio seçimi. Figtures rehberi — 2026 güncel.',
      en: 'Custom software development in Turkey: cost ranges, delivery process, team models, and how to choose a product studio. Figtures guide — updated 2026.',
    },
    heroTitle: {
      tr: 'Türkiye\'de özel yazılım geliştirme: maliyet, süre ve doğru partner nasıl seçilir?',
      en: 'Custom software in Turkey: how to budget, timeline, and choose the right partner',
    },
    directAnswer: {
      tr: 'Figtures, Türkiye merkezli global bir product studio olarak özel yazılım projelerini discovery → mimari → üretim → launch ritminde teslim eder. Tipik MVP süresi 8–14 hafta; maliyet kapsam, entegrasyon derinliği ve ekip büyüklüğüne göre değişir. Bu rehber, Türkiye pazarında doğru yazılım partneri seçmek için maliyet, süreç ve risk sinyallerini özetler.',
      en: 'Figtures is an Istanbul-based global product studio delivering custom software through discovery → architecture → production → launch. Typical MVP timelines run 8–14 weeks; budget depends on scope, integrations, and team shape. This guide summarizes cost, process, and risk signals for choosing a software partner in Turkey.',
    },
    primaryServiceSlug: 'custom-software-development',
    relatedServiceSlugs: ['custom-software-development', 'saas-mvp-development', 'startup-product-engineering', 'ui-ux-design'],
    relatedGuideSlugs: ['ai-development-company-turkey', 'saas-mvp-development-turkey'],
    relatedProjectSlugs: ['3d-yaninda', 'lcw', 'kolektif'],
    relatedBlogSlugs: ['custom-software-development-company-guide', 'mvp-development-company-guide'],
    sections: [
      {
        title: { tr: 'Özel yazılım ne zaman gerekir?', en: 'When do you need custom software?' },
        paragraphs: [
          {
            tr: 'Hazır SaaS ürünleri çoğu ekip için hızlı başlangıç sağlar; ancak iş akışınız benzersizse, entegrasyonlarınız karmaşıksa veya ürününüz veri/AI katmanında rekabet avantajı yaratıyorsa özel yazılım kaçınılmaz hale gelir. Türkiye\'de özellikle lojistik, perakende, üretim ve B2B SaaS ekipleri, Excel + WhatsApp + legacy ERP kombinasyonundan çıkmak için özel platform yatırımı yapar.',
            en: 'Off-the-shelf SaaS is a fast start for many teams, but custom software becomes necessary when workflows are unique, integrations are complex, or your product advantage lives in data/AI layers. In Turkey, logistics, retail, manufacturing, and B2B SaaS teams often invest in custom platforms to escape Excel + WhatsApp + legacy ERP combinations.',
          },
          {
            tr: 'Karar anında üç soru net olmalı: (1) Hangi kullanıcı akışı para kazandırıyor? (2) Hangi sistemlerle konuşmak zorundayız? (3) 12 ay sonra ölçek baskısı nereden gelecek? Bu sorulara cevap yoksa proje kapsamı şişer ve teslimat gecikir.',
            en: 'At decision time, three questions must be clear: (1) Which user flow drives revenue? (2) Which systems must we integrate? (3) Where will scale pressure hit in 12 months? Without answers, scope swells and delivery slips.',
          },
          {
            tr: 'Figtures olarak özel yazılımı "feature listesi" olarak değil, ürün hipotezi olarak ele alırız. Discovery haftasında kullanıcı yolculuğu, veri modeli ve release sırası netleşmeden kod yazmaya başlamayız.',
            en: 'At Figtures we treat custom software as a product hypothesis—not a feature list. We do not write production code until discovery clarifies user journeys, data models, and release sequencing.',
          },
        ],
      },
      {
        title: { tr: 'Türkiye pazarında maliyet aralıkları', en: 'Budget ranges in the Turkey market' },
        paragraphs: [
          {
            tr: '2026 itibarıyla Türkiye\'de özel yazılım MVP bütçeleri geniş bir aralıkta seyreder: odaklı bir internal tool için 4–8 haftalık sprintler, çoklu entegrasyonlu B2B platformlar için 12–20+ hafta. Fiyatı belirleyen asıl faktör ekran sayısı değil; domain karmaşıklığı, güvenlik gereksinimleri, ödeme/ERP entegrasyonları ve AI katmanıdır.',
            en: 'As of 2026, custom software MVP budgets in Turkey span a wide range: focused internal tools in 4–8 week sprints, multi-integration B2B platforms in 12–20+ weeks. Price is driven less by screen count and more by domain complexity, security requirements, payment/ERP integrations, and AI layers.',
          },
          {
            tr: 'Sağlıklı bir teklif şunları açıkça listeler: kapsam dışı maddeler, varsayımlar, değişiklik talebi süreci, SLA ve bakım modeli. "Sabit fiyat, sınırsız revizyon" vaadi genelde gizli teknik borç üretir.',
            en: 'A healthy proposal explicitly lists out-of-scope items, assumptions, change-request process, SLA, and maintenance model. "Fixed price, unlimited revisions" promises usually create hidden technical debt.',
          },
        ],
        bullets: [
          { tr: 'Discovery + mimari dokümantasyon (1–2 hafta)', en: 'Discovery + architecture documentation (1–2 weeks)' },
          { tr: 'MVP çekirdek akışlar + auth + temel admin', en: 'MVP core flows + auth + basic admin' },
          { tr: 'Staging/production CI, gözlemlenebilirlik, güvenlik temeli', en: 'Staging/production CI, observability, security baseline' },
        ],
      },
      {
        title: { tr: 'Doğru partneri seçerken kırmızı bayraklar', en: 'Red flags when choosing a partner' },
        paragraphs: [
          {
            tr: 'Teklifte kullanıcı problemi yerine sadece teknoloji listesi varsa, ekip ürün düşünmüyor olabilir. Discovery olmadan kesin teslim tarihi verilmesi, kapsam riskinin size yüklendiğini gösterir. Referans case study yoksa veya case study\'de metrik yoksa, teslimat kalitesi doğrulanamaz.',
            en: 'If a proposal lists technologies without user problems, the team may not think in products. A firm delivery date without discovery shifts scope risk to you. No reference case studies—or case studies without metrics—make quality impossible to verify.',
          },
          {
            tr: 'İyi bir product studio; mimari kararları, trade-off\'ları ve "yapmayacağımız şeyleri" yazılı olarak sunar. Kodun size ait olduğu, dokümantasyonun teslim edildiği ve post-launch destek modelinin net olduğu teklifler güven verir.',
            en: 'A strong product studio documents architecture decisions, trade-offs, and explicit non-goals. Proposals that guarantee code ownership, documentation handoff, and a clear post-launch support model earn trust.',
          },
        ],
      },
      {
        title: { tr: 'Figtures teslimat modeli', en: 'The Figtures delivery model' },
        paragraphs: [
          {
            tr: 'Discovery sprint ile başlarız: problem tanımı, kullanıcı segmentleri, başarı metrikleri ve release dilimleri. Ardından UX akışları ve veri modeli onaylanır; production kodu bu çerçevede yazılır. Haftalık demo ritmi sayesinde sürpriz teslimat olmaz.',
            en: 'We start with a discovery sprint: problem definition, user segments, success metrics, and release slices. UX flows and data models are approved before production code. Weekly demos prevent surprise deliveries.',
          },
          {
            tr: 'Türkiye ve global müşterilerle remote/async çalışırız; İstanbul saat diliminde overlap pencereleri planlarız. Ekip içi İngilizce/Türkçe dokümantasyon ve async karar kayıtları (ADR) standarttır.',
            en: 'We work remote/async with Turkey and global clients, planning overlap windows on Istanbul time. Bilingual documentation and async architecture decision records (ADRs) are standard.',
          },
          {
            tr: 'Launch sonrası kalite sprintleri: performans, güvenlik sertleştirme, analytics ve operasyon runbook. Amaç "canlıya aldık bitti" değil; ürününüzün ölçeklenebilir kalmasıdır.',
            en: 'Post-launch quality sprints cover performance, security hardening, analytics, and ops runbooks. The goal is not "we shipped"—it is a product that stays scalable.',
          },
        ],
      },
      {
        title: { tr: 'Teknoloji seçimi: hype değil ürün fit', en: 'Technology choices: product fit over hype' },
        paragraphs: [
          {
            tr: 'Türkiye\'de birçok teklif hâlâ "en yeni framework" etrafında dönüyor. Biz seçimi şu sırayla yaparız: ekip yetkinliği → entegrasyon gereksinimleri → ölçek profili → bakım maliyeti. React/Node, Python servisleri, PostgreSQL ve bulut-native dağıtım çoğu B2B ürünü için yeterlidir.',
            en: 'Many Turkey proposals still revolve around "the newest framework." We choose stack in this order: team skill → integration needs → scale profile → maintenance cost. React/Node, Python services, PostgreSQL, and cloud-native deploys suffice for most B2B products.',
          },
          {
            tr: 'AI katmanı gerekiyorsa, model seçimini ürün metriklerine bağlarız: doğruluk, gecikme, maliyet/istek ve fallback davranışı. PoC\'yi production guardrail\'leri olmadan canlıya almayız.',
            en: 'When AI is required, we tie model choice to product metrics: accuracy, latency, cost/request, and fallback behavior. We do not ship PoCs to production without guardrails.',
          },
        ],
      },
      {
        title: { tr: 'Hukuk, KVKK ve güvenlik temeli', en: 'Legal, KVKK, and security baseline' },
        paragraphs: [
          {
            tr: 'Türkiye\'de KVKK uyumu ve sözleşme disiplini proje başarısını doğrudan etkiler. Kişisel veri işleyen ürünlerde veri minimizasyonu, saklama süresi ve erişim logları tasarımın parçası olmalıdır. Figtures projelerinde güvenlik; auth, yetkilendirme, audit log ve secrets yönetimi ile başlar.',
            en: 'In Turkey, KVKK compliance and contract discipline directly affect project success. Products processing personal data need data minimization, retention, and access logs designed in—not bolted on. At Figtures, security starts with auth, authorization, audit logs, and secrets management.',
          },
        ],
      },
      {
        title: { tr: 'Türkiye\'de ekip modeli: in-house vs outsourced product studio', en: 'Team models in Turkey: in-house vs outsourced product studio' },
        paragraphs: [
          {
            tr: 'In-house ekip uzun vadeli ürün sahipliği için idealdir; ancak hiring cycle ve senior mühendis bulma süresi MVP hızını yavaşlatır. Outsourced product studio modeli, discovery’dan launch’a kadar hazır bir ritim sunar ve launch sonrası in-house’a devredilebilir.',
            en: 'In-house teams excel at long-term product ownership, but hiring cycles and senior engineer scarcity slow MVP velocity. An outsourced product studio ships a ready discovery-to-launch cadence and can transition to in-house post-launch.',
          },
          {
            tr: 'Hibrit model sık görülür: Figtures core MVP’yi teslim eder, sizin ekibiniz feature backlog’u devralır. Bu modelde dokümantasyon, ADR ve kod kalitesi devralma maliyetini düşürür.',
            en: 'Hybrid is common: Figtures delivers the core MVP, your team owns the feature backlog. Documentation, ADRs, and code quality reduce handoff cost.',
          },
        ],
      },
      {
        title: { tr: 'RFP ve teklif değerlendirme checklist\'i', en: 'RFP and proposal evaluation checklist' },
        paragraphs: [
          {
            tr: 'Teklifleri puanlarken beş alan kullanın: (1) discovery derinliği, (2) mimari açıklık, (3) benzer case study kanıtı, (4) güvenlik/KVKK yaklaşımı, (5) launch sonrası plan. En düşük fiyat yerine en düşük toplam riski seçin.',
            en: 'Score proposals on five areas: (1) discovery depth, (2) architecture clarity, (3) similar case proof, (4) security/privacy approach, (5) post-launch plan. Pick lowest total risk—not lowest price.',
          },
          {
            tr: 'Figtures tekliflerinde kapsam dışı liste, varsayımlar ve haftalık demo takvimi standarttır. Bu şeffaflık, proje ortasında sürpriz maliyet riskini azaltır.',
            en: 'Figtures proposals include out-of-scope lists, assumptions, and weekly demo calendars by default—reducing surprise cost mid-project.',
          },
        ],
      },
      {
        title: { tr: '2026 trendleri: AI, entegrasyon ve compliance', en: '2026 trends: AI, integrations, and compliance' },
        paragraphs: [
          {
            tr: 'Türkiye’de özel yazılım projelerinin yüzde artan kısmı AI-assisted workflow veya veri pipeline içeriyor. Bu, sadece chatbot eklemek değil; karar noktalarına güvenilir öneri ve otomasyon katmanı eklemektir.',
            en: 'A growing share of custom software projects in Turkey include AI-assisted workflows or data pipelines—not just chatbots, but reliable recommendations and automation at decision points.',
          },
          {
            tr: 'ERP, CRM, ödeme ve lojistik entegrasyonları proje süresinin önemli bölümünü oluşturur. API-first mimari ve idempotent webhook tasarımı erken aşamada planlanmalıdır.',
            en: 'ERP, CRM, payment, and logistics integrations consume significant timeline. API-first architecture and idempotent webhooks should be planned early.',
          },
        ],
      },
    ],
    comparisonRows: [
      {
        aspect: { tr: 'Kapsam netliği', en: 'Scope clarity' },
        alternative: { tr: 'Belirsiz feature listesi', en: 'Vague feature laundry list' },
        figtures: { tr: 'Discovery + yazılı kapsam dışı liste', en: 'Discovery + written out-of-scope list' },
      },
      {
        aspect: { tr: 'Teslim ritmi', en: 'Delivery cadence' },
        alternative: { tr: 'Tek seferde final demo', en: 'Single final demo' },
        figtures: { tr: 'Haftalık çalışan demo + ADR', en: 'Weekly working demos + ADRs' },
      },
      {
        aspect: { tr: 'Kod mülkiyeti', en: 'Code ownership' },
        alternative: { tr: 'Belirsiz / vendor lock-in', en: 'Unclear / vendor lock-in' },
        figtures: { tr: 'Tam kaynak + dokümantasyon devri', en: 'Full source + documentation handoff' },
      },
    ],
    timelinePhases: [
      {
        phase: { tr: 'Discovery & UX', en: 'Discovery & UX' },
        duration: { tr: '1–2 hafta', en: '1–2 weeks' },
        deliverable: { tr: 'Akışlar, veri modeli, release planı', en: 'Flows, data model, release plan' },
      },
      {
        phase: { tr: 'MVP Build', en: 'MVP Build' },
        duration: { tr: '6–12 hafta', en: '6–12 weeks' },
        deliverable: { tr: 'Production-ready çekirdek ürün', en: 'Production-ready core product' },
      },
      {
        phase: { tr: 'Launch & Hardening', en: 'Launch & Hardening' },
        duration: { tr: '1–2 hafta', en: '1–2 weeks' },
        deliverable: { tr: 'CI/CD, monitoring, runbook', en: 'CI/CD, monitoring, runbook' },
      },
    ],
    faqs: [
      {
        question: { tr: 'Türkiye\'de özel yazılım MVP ne kadar sürer?', en: 'How long does a custom software MVP take in Turkey?' },
        answer: { tr: 'Odaklı ürünlerde 8–14 hafta gerçekçidir; çoklu entegrasyon ve compliance gereksinimleri süreyi uzatır.', en: '8–14 weeks is realistic for focused products; multiple integrations and compliance extend timelines.' },
      },
      {
        question: { tr: 'Freelancer mı ajans mı product studio mu?', en: 'Freelancer vs agency vs product studio?' },
        answer: { tr: 'Freelancer hızlı prototip için uygun; ürün + mimari + AI birlikte gerekiyorsa product studio daha düşük risklidir.', en: 'Freelancers suit quick prototypes; when product + architecture + AI ship together, a product studio lowers risk.' },
      },
      {
        question: { tr: 'Figtures hangi sektörlerde çalışıyor?', en: 'Which industries does Figtures serve?' },
        answer: { tr: 'Perakende, lojistik, B2B SaaS, AI-enabled ürünler ve kurumsal dijital dönüşüm projeleri.', en: 'Retail, logistics, B2B SaaS, AI-enabled products, and enterprise digital transformation.' },
      },
      {
        question: { tr: 'Mevcut ekibimizle birlikte çalışabilir misiniz?', en: 'Can you work alongside our team?' },
        answer: { tr: 'Evet — embedded modelde sizin PM/eng kadronuzla aynı sprint ritminde ilerleriz.', en: 'Yes — in embedded mode we share sprint cadence with your PM/engineering team.' },
      },
    ],
  },
  {
    slug: 'ai-development-company-turkey',
    title: {
      tr: 'Türkiye AI Geliştirme Şirketi Seçim Rehberi',
      en: 'How to Choose an AI Development Company in Turkey',
    },
    seoTitle: {
      tr: 'Türkiye’de AI Geliştirme Şirketi Seçimi: 2026 Rehberi',
      en: 'Choosing an AI Development Partner in Turkey: 2026 Guide',
    },
    seoDescription: {
      tr: 'Türkiye\'de AI geliştirme partneri seçimi: PoC vs production, maliyet, guardrail ve teslimat sinyalleri. Figtures AI product studio rehberi.',
      en: 'Choosing an AI development partner in Turkey: PoC vs production, cost, guardrails, and delivery signals. Figtures AI product studio guide.',
    },
    heroTitle: {
      tr: 'Türkiye\'de production-grade AI geliştirme partneri nasıl seçilir?',
      en: 'How to pick a production-grade AI development partner in Turkey',
    },
    directAnswer: {
      tr: 'Figtures, LLM özellikleri, AI agent workflow\'ları ve RAG sistemlerini ürününüze entegre eden bir AI product studio\'dur. PoC değil; evaluation, guardrail ve maliyet kontrolü ile production teslimatına odaklanırız.',
      en: 'Figtures is an AI product studio integrating LLM features, agent workflows, and RAG into your product—with evaluation, guardrails, and cost control for production—not demo-only PoCs.',
    },
    primaryServiceSlug: 'ai-development',
    relatedServiceSlugs: ['ai-development', 'ai-automation-agents', 'custom-software-development'],
    relatedGuideSlugs: ['custom-software-development-turkey', 'saas-mvp-development-turkey'],
    relatedProjectSlugs: ['ztech', 'lcw'],
    relatedBlogSlugs: ['ai-development-company-selection-guide', 'chatgpt-integration-business-guide'],
    sections: [
      {
        title: { tr: 'PoC ile production arasındaki fark', en: 'PoC vs production' },
        paragraphs: [
          {
            tr: 'Türkiye\'de AI projelerinin çoğu PoC aşamasında kalır çünkü eval, fallback, maliyet ve güvenlik katmanları planlanmaz. Production AI; prompt + retrieval + tool-call + monitoring + sürümleme disiplini gerektirir.',
            en: 'Most AI projects in Turkey stall at PoC because eval, fallback, cost, and security layers are unplanned. Production AI needs prompt + retrieval + tool-call + monitoring + versioning discipline.',
          },
          {
            tr: 'İyi bir AI partneri "hangi model" sorusundan önce "hangi kullanıcı kararı hızlanacak?" sorusunu sorar. Model seçimi metrikle gelir: doğruluk, latency, token maliyeti.',
            en: 'A strong AI partner asks which user decision accelerates before which model. Model choice follows metrics: accuracy, latency, token cost.',
          },
        ],
      },
      {
        title: { tr: 'LLM, agent ve otomasyon: ne zaman hangisi?', en: 'LLM, agents, automation: when to use what' },
        paragraphs: [
          {
            tr: 'Chat assistant kullanıcıya yanıt üretir; agent workflow birden fazla adımı ve tool\'u yönetir; otomasyon tekrarlayan operasyonu insan onayıyla hızlandırır. Ürününüzde üçünü karıştırmadan önce tek bir yüksek değerli akış seçin.',
            en: 'Chat assistants generate answers; agent workflows orchestrate multi-step tool use; automation accelerates repeatable ops with human approval. Pick one high-value flow before blending all three.',
          },
        ],
      },
      {
        title: { tr: 'Maliyet ve token ekonomisi', en: 'Cost and token economics' },
        paragraphs: [
          {
            tr: 'AI maliyeti sadece geliştirme değil; inference burn rate\'tir. Cache, routing, küçük modeller, batch işleme ve context trimming production\'da zorunludur. Figtures her feature için maliyet tavanı ve alarm kurar.',
            en: 'AI cost is not just build—it is inference burn rate. Caching, routing, smaller models, batching, and context trimming are production requirements. Figtures sets cost ceilings and alerts per feature.',
          },
        ],
      },
      {
        title: { tr: 'Güvenlik, KVKK ve veri sızıntısı önleme', en: 'Security, privacy, and leakage prevention' },
        paragraphs: [
          {
            tr: 'Kurumsal AI\'da PII redaksiyonu, prompt injection savunması ve audit log standarttır. Türkiye operasyonlarında veri residency ve KVKK süreçleri discovery\'de netleştirilir.',
            en: 'Enterprise AI needs PII redaction, prompt-injection defense, and audit logs. For Turkey operations, data residency and KVKK processes are clarified in discovery.',
          },
        ],
      },
      {
        title: { tr: 'Figtures AI teslimat çerçevesi', en: 'Figtures AI delivery framework' },
        paragraphs: [
          {
            tr: 'Discovery → eval set → guardrail → entegrasyon → hardening sırasıyla ilerleriz. Her sprint sonunda gerçek ürün verisiyle ölçülen demo sunarız, slayt değil.',
            en: 'We progress discovery → eval set → guardrails → integration → hardening. Each sprint ends with demos measured on real product data—not slides.',
          },
        ],
      },
    ],
    comparisonRows: [
      {
        aspect: { tr: 'Eval disiplini', en: 'Eval discipline' },
        alternative: { tr: 'Manuel "güzel görünüyor" testi', en: 'Manual "looks good" testing' },
        figtures: { tr: 'Senaryo seti + regression + cost tracking', en: 'Scenario sets + regression + cost tracking' },
      },
      {
        aspect: { tr: 'Ürün entegrasyonu', en: 'Product integration' },
        alternative: { tr: 'Ayrı chat widget', en: 'Detached chat widget' },
        figtures: { tr: 'Core UX akışına gömülü AI', en: 'AI embedded in core UX flows' },
      },
    ],
    timelinePhases: [
      { phase: { tr: 'Use-case discovery', en: 'Use-case discovery' }, duration: { tr: '1 hafta', en: '1 week' }, deliverable: { tr: 'Metrik + risk matrisi', en: 'Metrics + risk matrix' } },
      { phase: { tr: 'Production sprint', en: 'Production sprint' }, duration: { tr: '4–8 hafta', en: '4–8 weeks' }, deliverable: { tr: 'Canlı AI feature', en: 'Live AI feature' } },
    ],
    faqs: [
      {
        question: { tr: 'ChatGPT entegrasyonu yeterli mi?', en: 'Is ChatGPT integration enough?' },
        answer: { tr: 'Entegrasyon başlangıçtır; eval, fallback ve maliyet kontrolü olmadan production risklidir.', en: 'Integration is a start—production is risky without eval, fallback, and cost control.' },
      },
      {
        question: { tr: 'Hangi modelleri destekliyorsunuz?', en: 'Which models do you support?' },
        answer: { tr: 'OpenAI, Anthropic ve açık kaynak modeller; seçim use-case metriklerine göre yapılır.', en: 'OpenAI, Anthropic, and open models—chosen by use-case metrics.' },
      },
    ],
  },
  {
    slug: 'saas-mvp-development-turkey',
    title: {
      tr: 'Türkiye SaaS MVP Geliştirme Rehberi',
      en: 'SaaS MVP Development in Turkey — Guide',
    },
    seoTitle: {
      tr: 'SaaS MVP Rehberi: Türkiye’de Süre, Maliyet ve Mimari | Figtures',
      en: 'SaaS MVP Guide for Turkey: Timeline, Cost & Architecture | Figtures',
    },
    seoDescription: {
      tr: 'Türkiye\'de SaaS MVP: multi-tenant mimari, fiyatlandırma, launch checklist ve product studio seçimi. Figtures 2026 rehberi.',
      en: 'SaaS MVP in Turkey: multi-tenant architecture, pricing, launch checklist, and studio selection. Figtures 2026 guide.',
    },
    heroTitle: {
      tr: 'Türkiye\'de SaaS MVP\'yi kaç haftada ve hangi mimariyle çıkarmalısınız?',
      en: 'How fast—and with what architecture—should you ship a SaaS MVP in Turkey?',
    },
    directAnswer: {
      tr: 'Figtures, SaaS MVP\'leri 8–12 haftalık odaklı sprintlerle teslim eder: auth, billing hazırlığı, core value akışı ve gözlemlenebilirlik. Multi-tenant karmaşıklığı discovery\'de netleştirilir; erken aşamada gereksiz ölçek mimarisi önermeyiz.',
      en: 'Figtures ships SaaS MVPs in focused 8–12 week sprints: auth, billing readiness, core value flow, and observability. Multi-tenant complexity is clarified in discovery—we avoid premature scale architecture.',
    },
    primaryServiceSlug: 'saas-mvp-development',
    relatedServiceSlugs: ['saas-mvp-development', 'startup-product-engineering', 'custom-software-development'],
    relatedGuideSlugs: ['custom-software-development-turkey', 'mobile-app-development-turkey'],
    relatedProjectSlugs: ['ztech', 'bool'],
    relatedBlogSlugs: ['saas-development-cost-guide', 'mvp-development-company-guide'],
    sections: [
      {
        title: { tr: 'SaaS MVP kapsamı nasıl kesilir?', en: 'How to slice SaaS MVP scope' },
        paragraphs: [
          {
            tr: 'İlk sürümde tek persona, tek core workflow, tek gelir hipotezi yeterlidir. Admin paneli, raporlama ve entegrasyonlar ikinci faza kalır. Bu disiplin Türkiye startup ekosisteminde runway\'i korur.',
            en: 'Ship one persona, one core workflow, one revenue hypothesis first. Admin, reporting, and integrations wait for phase two. This discipline preserves runway in Turkey\'s startup ecosystem.',
          },
        ],
      },
      {
        title: { tr: 'Multi-tenant ve veri izolasyonu', en: 'Multi-tenant and data isolation' },
        paragraphs: [
          {
            tr: 'Erken aşamada schema-per-tenant veya row-level isolation seçimi ürün segmentine bağlıdır. B2B enterprise için audit ve SSO hazırlığı MVP\'de planlanmalıdır.',
            en: 'Early schema-per-tenant vs row-level isolation depends on segment. B2B enterprise MVPs should plan audit and SSO readiness.',
          },
        ],
      },
      {
        title: { tr: 'Fiyatlandırma ve billing hazırlığı', en: 'Pricing and billing readiness' },
        paragraphs: [
          {
            tr: 'MVP\'de tam billing entegrasyonu şart değil; ancak plan yapısı ve usage metrikleri kodda yer açacak şekilde tasarlanmalıdır. Stripe, iyzico, PayTR veya Paddle\'dan hangisiyle entegre olunacağı launch öncesi netleştirilir.',
            en: 'Full billing in MVP is optional—but plan structure and usage metrics need code hooks. Which provider to integrate (Stripe, iyzico, PayTR or Paddle) is settled pre-launch.',
          },
        ],
      },
      {
        title: { tr: 'Launch checklist', en: 'Launch checklist' },
        paragraphs: [
          {
            tr: 'Staging=production parity, error tracking, backup, status page, onboarding analytics ve support kanalı launch öncesi tamamlanır. Figtures her MVP için runbook teslim eder.',
            en: 'Staging=production parity, error tracking, backups, status page, onboarding analytics, and support channel are pre-launch. Figtures delivers a runbook with every MVP.',
          },
        ],
      },
    ],
    comparisonRows: [
      {
        aspect: { tr: 'Mimari', en: 'Architecture' },
        alternative: { tr: 'Erken microservices', en: 'Early microservices' },
        figtures: { tr: 'Modüler monolith → ölçekte ayrışma', en: 'Modular monolith → split at scale' },
      },
    ],
    timelinePhases: [
      { phase: { tr: 'Scope lock', en: 'Scope lock' }, duration: { tr: '1 hafta', en: '1 week' }, deliverable: { tr: 'MVP cut + metrics', en: 'MVP cut + metrics' } },
      { phase: { tr: 'Build', en: 'Build' }, duration: { tr: '6–10 hafta', en: '6–10 weeks' }, deliverable: { tr: 'Paying-ready MVP', en: 'Paying-ready MVP' } },
    ],
    faqs: [
      {
        question: { tr: 'SaaS MVP için minimum ekip?', en: 'Minimum team for SaaS MVP?' },
        answer: { tr: 'Product-minded tech lead + full-stack + UX touchpoint; Figtures bu kadroyu tek studio altında sağlar.', en: 'Product-minded tech lead + full-stack + UX touchpoints—Figtures provides this as one studio.' },
      },
    ],
  },
  {
    slug: 'mobile-app-development-turkey',
    title: {
      tr: 'Türkiye Mobil Uygulama Geliştirme Rehberi',
      en: 'Mobile App Development in Turkey — Guide',
    },
    seoTitle: {
      tr: 'Mobil Uygulama Rehberi: Native mi Cross-platform mu, Maliyet | Figtures',
      en: 'Mobile App Guide for Turkey: Native vs Cross-platform, Cost | Figtures',
    },
    seoDescription: {
      tr: 'Türkiye\'de iOS/Android uygulama geliştirme: React Native vs native, maliyet, store launch ve bakım. Figtures rehberi.',
      en: 'iOS/Android app development in Turkey: React Native vs native, cost, store launch, and maintenance. Figtures guide.',
    },
    heroTitle: {
      tr: 'Türkiye\'de mobil uygulama: native mi cross-platform mu, ne zaman?',
      en: 'Mobile apps in Turkey: native vs cross-platform—and when?',
    },
    directAnswer: {
      tr: 'Figtures, React Native ve native modüllerle iOS/Android MVP\'leri projeye göre 1–2 haftadan 6–8 haftaya kadar sürede teslim eder. Seçim; performans ihtiyacı, cihaz API kullanımı ve ekip hızına göre discovery\'de yapılır.',
      en: 'Figtures delivers iOS/Android MVPs with React Native and native modules in anywhere from 1–2 weeks to 6–8 weeks, depending on the project. The choice follows performance needs, device APIs, and team velocity—decided in discovery.',
    },
    primaryServiceSlug: 'mobile-app-development',
    relatedServiceSlugs: ['mobile-app-development', 'ui-ux-design', 'startup-product-engineering'],
    relatedGuideSlugs: ['saas-mvp-development-turkey', 'custom-software-development-turkey'],
    relatedProjectSlugs: ['outfica', 'ev-arkadasi'],
    relatedBlogSlugs: ['mobile-app-development-cost-guide', 'mobile-app-performance-optimization'],
    sections: [
      {
        title: { tr: 'Native vs cross-platform kararı', en: 'Native vs cross-platform' },
        paragraphs: [
          {
            tr: 'React Native çoğu B2B ve consumer MVP için yeterli hızdadır. Ağır grafik, AR veya düşük seviye sensör ihtiyacında native modül eklenir. Türkiye\'de iki platformu ayrı ekiplerle çıkarmak maliyeti ikiye katlar.',
            en: 'React Native is fast enough for most B2B and consumer MVPs. Heavy graphics, AR, or low-level sensors get native modules. Shipping two platforms with separate teams in Turkey doubles cost.',
          },
        ],
      },
      {
        title: { tr: 'Store launch ve ASO temeli', en: 'Store launch and ASO basics' },
        paragraphs: [
          {
            tr: 'App Store ve Play Console metadata, screenshot seti, privacy nutrition labels ve review notları launch checklist\'inin parçasıdır. Figtures store submission sürecini yönetir.',
            en: 'Store metadata, screenshots, privacy labels, and review notes are launch checklist items. Figtures manages store submission.',
          },
        ],
      },
      {
        title: { tr: 'Offline-first ve performans', en: 'Offline-first and performance' },
        paragraphs: [
          {
            tr: 'Mobil kullanıcılar zayıf ağda uygulamayı terk eder. Offline cache, optimistic UI ve crash-free session oranı MVP metriklerine dahil edilmelidir.',
            en: 'Mobile users churn on weak networks. Offline cache, optimistic UI, and crash-free sessions belong in MVP metrics.',
          },
        ],
      },
    ],
    comparisonRows: [
      {
        aspect: { tr: 'Platform kapsamı', en: 'Platform coverage' },
        alternative: { tr: 'İki ayrı native ekip', en: 'Two separate native teams' },
        figtures: { tr: 'RN çekirdek + hedefli native modül', en: 'RN core + targeted native modules' },
      },
    ],
    timelinePhases: [
      { phase: { tr: 'UX + prototype', en: 'UX + prototype' }, duration: { tr: 'Birkaç gün – 2 hafta', en: 'A few days to 2 weeks' }, deliverable: { tr: 'Tıklanabilir akış', en: 'Clickable flows' } },
      { phase: { tr: 'MVP build', en: 'MVP build' }, duration: { tr: 'Toplam 1–2 haftadan 6–8 haftaya', en: '1–2 to 6–8 weeks in total' }, deliverable: { tr: 'Store-ready build', en: 'Store-ready build' } },
    ],
    faqs: [
      {
        question: { tr: 'Mobil MVP maliyeti web\'e göre neden yüksek?', en: 'Why is mobile MVP cost higher than web?' },
        answer: { tr: 'Store süreçleri, cihaz test matrisi ve release pipeline ek yük getirir.', en: 'Store processes, device matrices, and release pipelines add overhead.' },
      },
    ],
  },
];

const guideMap = new Map(guides.map((g) => [g.slug, g]));

export function getPillarGuideBySlug(slug: string) {
  return guideMap.get(slug) ?? null;
}

export function getAllPillarGuides() {
  return guides;
}

export function getFeaturedPillarGuideSlugs() {
  return ['custom-software-development-turkey', 'ai-development-company-turkey'];
}

export function getPillarGuidesBySlugs(slugs: string[]) {
  return slugs.map((slug) => guideMap.get(slug)).filter((g): g is PillarGuide => Boolean(g));
}

export function estimateRenderedGuideWordCount(guide: PillarGuide, lang: Locale): number {
  const service = getServiceBySlug(guide.primaryServiceSlug);
  let count = estimateGuideWordCount(guide, lang);
  if (!service) return count;

  const serviceChunks = [
    ...service.deliverables.map((item) => localizeServiceText(item, lang)),
    ...service.process.flatMap((step) => [
      localizeServiceText(step.title, lang),
      localizeServiceText(step.description, lang),
    ]),
    ...service.faqs.flatMap((faq) => [
      localizeServiceText(faq.question, lang),
      localizeServiceText(faq.answer, lang),
    ]),
  ];
  count += serviceChunks.join(' ').split(/\s+/).filter(Boolean).length;
  return count;
}

export function getPillarGuideForServiceSlug(serviceSlug: string) {
  return guides.find((guide) => guide.primaryServiceSlug === serviceSlug) ?? null;
}
